import { cn } from '@/lib/utils';
import * as React from 'react';

import tokens from '../../../../backend/design-tokens.json';

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(({ className, ...props }, ref) => {
  const tableStyles = 'w-full caption-bottom text-sm';
  const webContainerStyles = 'border border-neutral-200 rounded-lg bg-neutral-50/10';
  const containerDivStyles = webContainerStyles;

  return (
    <div className={cn('relative w-full overflow-auto h-full', containerDivStyles)}>
      <table ref={ref} className={cn(tableStyles, className)} {...props} />
    </div>
  );
});

Table.displayName = 'Table';

const TableBorderless = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(({ className, ...props }, ref) => (
  <div className={cn('relative w-full overflow-auto')}>
    <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
  </div>
));
TableBorderless.displayName = 'TableBorderless';

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => {
    const headerStyles = 'text-neutral-800 text-base';
    return <thead ref={ref} className={cn('text-left align-middle font-semibold', headerStyles, className)} {...props} />;
  }
);

TableHeader.displayName = 'TableHeader';

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(({ className, ...props }, ref) => {
  const headStyles = 'p-4 text-neutral-800 text-base ';
  return <th ref={ref} className={cn('h-12 font-semibold text-left align-middle', headStyles, className)} {...props} />;
});

TableHead.displayName = 'TableHead';

const TableHeadSmall = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => {
    return (
      <th
        ref={ref}
        className={cn('h-8 px-2 text-brand text-left align-middle text-sm font-semibold [&:has([role=checkbox])]:pr-0', className)}
        {...props}
      />
    );
  }
);

TableHeadSmall.displayName = 'TableHeadSmall';

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => {
    const borderStyle = 'border-neutral-200 p-5';

    return <tbody ref={ref} className={cn(borderStyle, '[&_tr:last-child]:border-0', className)} {...props} />;
  }
);

TableBody.displayName = 'TableBody';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(({ className, ...props }, ref) => {
  return (
    <tr
      ref={ref}
      className={cn('border-b border-neutral-25 transition-colors data-[state=selected]:bg-neutral-100', className)}
      {...props}
    />
  );
});

TableRow.displayName = 'TableRow';

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, children, ...props }, ref) => {
    const cellStyles = 'px-4 py-5 text-base';

    const isNumeric = (value: unknown): boolean => {
      if (value === null || value === undefined || value === '') return false;
      return !isNaN(Number(value));
    };

    const alignmentClass =
      (typeof children === 'string' || typeof children === 'number') && isNumeric(children) ? tokens.decimal_alignment : '';

    return (
      <td ref={ref} className={cn('align-middle text-neutral-750', cellStyles, alignmentClass, 'text-left', className)} {...props}>
        <div className="w-full">{children}</div>
      </td>
    );
  }
);

TableCell.displayName = 'TableCell';

const TableCellSmall = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => {
    return <td ref={ref} className={cn('p-2 align-middle text-xs text-neutral-750 [&:has([role=checkbox])]:pr-0', className)} {...props} />;
  }
);

TableCellSmall.displayName = 'TableCellSmall';

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <tfoot ref={ref} className={cn('border-t font-small [&>tr]:last:border-b-0 ', className)} {...props} />
);
TableFooter.displayName = 'TableFooter';

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => <caption ref={ref} className={cn('mt-4 text-sm text-neutral-500', className)} {...props} />
);
TableCaption.displayName = 'TableCaption';

export { Table, TableBorderless, TableHeader, TableHead, TableHeadSmall, TableBody, TableRow, TableCell, TableCellSmall, TableCaption };
